import { Clock, Copy, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import type { DocumentPolicy } from '@/types/documentPolicy.types';

interface PolicyTableProps {
  policies: DocumentPolicy[];
  isLoading: boolean;
  onEdit: (policy: DocumentPolicy) => void;
  onClone: (policy: DocumentPolicy) => void;
  onHistory: (policy: DocumentPolicy) => void;
}

function SkeletonRow() {
  return (
    <tr className='animate-pulse'>
      <td className='px-6 py-4'>
        <div className='h-4 w-32 rounded bg-gray-200' />
      </td>
      <td className='px-6 py-4'>
        <div className='h-4 w-24 rounded bg-gray-200' />
      </td>
      <td className='px-6 py-4'>
        <div className='h-4 w-16 rounded bg-gray-200' />
      </td>
      <td className='px-6 py-4'>
        <div className='h-4 w-8 rounded bg-gray-200' />
      </td>
      <td className='px-6 py-4'>
        <div className='h-4 w-12 rounded bg-gray-200' />
      </td>
      <td className='px-6 py-4'>
        <div className='h-4 w-20 rounded bg-gray-200' />
      </td>
      <td className='px-6 py-4'>
        <div className='h-4 w-16 rounded bg-gray-200' />
      </td>
    </tr>
  );
}

export function PolicyTable({ policies, isLoading, onEdit, onClone, onHistory }: PolicyTableProps) {
  const { t } = useTranslation();

  const columns = [
    { key: 'name', label: t('documentPolicies.columns.name') },
    { key: 'role', label: t('documentPolicies.columns.role') },
    { key: 'site', label: t('documentPolicies.columns.site') },
    { key: 'requiredTypes', label: t('documentPolicies.columns.requiredTypes') },
    { key: 'ocrEnabled', label: t('documentPolicies.columns.ocr') },
    { key: 'createdAt', label: t('documentPolicies.columns.createdAt') },
    { key: 'actions', label: t('documentPolicies.columns.actions') },
  ];

  if (isLoading) {
    return (
      <table className='w-full table-fixed divide-y divide-gray-200'>
        <thead className='bg-gray-50'>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-200 bg-white'>
          {Array.from({ length: 5 }, (_, i) => (
            <SkeletonRow key={i} />
          ))}
        </tbody>
      </table>
    );
  }

  if (policies.length === 0) {
    return <p className='px-6 py-8 text-center text-gray-500'>{t('documentPolicies.noResults')}</p>;
  }

  return (
    <table className='w-full table-fixed divide-y divide-gray-200'>
      <thead className='bg-gray-50'>
        <tr>
          {columns.map((col) => (
            <th
              key={col.key}
              className={cn(
                'px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500',
                col.key === 'actions' && 'w-28 text-right',
                col.key === 'requiredTypes' && 'w-28',
                col.key === 'ocrEnabled' && 'w-20',
              )}>
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className='divide-y divide-gray-200 bg-white'>
        {policies.map((policy) => (
          <tr
            key={policy.policyId}
            className='cursor-pointer transition-colors hover:bg-blue-50'
            onClick={() => onEdit(policy)}>
            <td className='truncate px-6 py-4 text-sm font-medium text-gray-900'>{policy.name}</td>
            <td className='px-6 py-4 text-sm text-gray-600'>{t(`jobPostings.roles.${policy.role}`, policy.role)}</td>
            <td className='px-6 py-4 text-sm text-gray-600'>
              {policy.site === '_all' ? t('documentPolicies.allSites') : policy.site}
            </td>
            <td className='px-6 py-4 text-sm text-gray-600'>{policy.requiredTypes.length}</td>
            <td className='px-6 py-4 text-sm text-gray-600'>
              <span
                className={cn(
                  'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                  policy.ocrEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600',
                )}>
                {policy.ocrEnabled ? t('common.yes') : t('common.no')}
              </span>
            </td>
            <td className='px-6 py-4 text-sm text-gray-500'>{new Date(policy.createdAt).toLocaleDateString()}</td>
            <td className='px-6 py-4 text-right'>
              <div className='flex items-center justify-end gap-1'>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(policy);
                  }}
                  className='rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600'
                  title={t('documentPolicies.actions.edit')}>
                  <Pencil className='h-4 w-4' />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClone(policy);
                  }}
                  className='rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600'
                  title={t('documentPolicies.actions.clone')}>
                  <Copy className='h-4 w-4' />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onHistory(policy);
                  }}
                  className='rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600'
                  title={t('documentPolicies.actions.history')}>
                  <Clock className='h-4 w-4' />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
